
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search } from 'lucide-react';
import { keycloakLogout, useAuthStore, useWikiAccess } from '@/features/auth';
import { useThemeStore } from '@/features/theme';
import { PersonIcon, TaskIcon, TicketIcon } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { Dialog, DialogContent, EmptyState, Scroller, SearchInput, Skeleton, toast } from '@/shared/ui';
import { buildCommands, type Command } from '../model/commands';
import { matchCommand } from '../model/match';
import { useCommandPaletteStore } from '../model/palette-store';
import {
  bumpUsage,
  clearRecent,
  loadRecent,
  loadUsage,
  pushRecent,
  removeRecent,
  usageBoost,
  type RecentEntry,
} from '../model/recent';
import { cycleScope, looksLikeTaskKey, looksLikeTicketNumber, SCOPES, takePrefix, type Scope } from '../model/scopes';
import type { PaletteGroup, PaletteItem } from '../model/types';
import { hasRemoteSources, MIN_QUERY, settledKey, usePaletteSearch } from '../model/use-palette-search';
import { PalettePreview } from './PalettePreview';
import { PaletteRow, type PickMode } from './PaletteRow';
import styles from './CommandPalette.module.css';

const GROUP_TITLES: Record<PaletteGroup, string> = {
  recent: 'Недавнее',
  mytasks: 'Мои задачи в работе',
  commands: 'Команды',
  create: 'Создать',
  nav: 'Разделы',
  admin: 'Администрирование',
  prefs: 'Оформление',
  account: 'Аккаунт',
  tickets: 'Заявки',
  tasks: 'Задачи',
  wiki: 'Инструкции',
  people: 'Люди',
  more: 'Искать дальше',
};

const SECTION_ORDER = ['create', 'nav', 'admin', 'prefs', 'account'] as const;

const EMPTY_HINTS: Record<Scope, { title: string; hint: string }> = {
  all: {
    title: 'Что ищем?',
    hint: 'Номер или тему заявки, ключ задачи, инструкцию, человека или команду. Префиксы: > команды, # заявки, ! задачи, ? инструкции, @ люди.',
  },
  commands: { title: 'Команды', hint: 'Раздел, действие или настройка — например «тема» или «новое».' },
  tickets: { title: 'Поиск заявок', hint: 'Номер, тема или имя заявителя.' },
  tasks: { title: 'Поиск задач', hint: 'Ключ вроде DEV-12, название или имя исполнителя.' },
  wiki: { title: 'Поиск по инструкциям', hint: 'Слова из названия или текста статьи.' },
  people: { title: 'Поиск людей', hint: 'Имя, логин или почта.' },
};

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = IS_MAC ? '⌘' : 'Ctrl+';

const RECENT_ICONS: Record<Exclude<RecentEntry['kind'], 'command'>, PaletteItem['icon']> = {
  ticket: TicketIcon,
  task: TaskIcon,
  wiki: FileText,
  person: PersonIcon,
};

function commandToItem(c: Command, group: PaletteGroup, ranges?: PaletteItem['ranges']): PaletteItem {
  return {
    id: c.id,
    group,
    kind: 'command',
    icon: c.icon,
    label: c.label,
    ranges,
    hint: c.hint,
    description: c.description,
    to: c.to,
    run: c.run,
    stay: c.stay,
  };
}

const absoluteUrl = (to: string) => new URL(to, window.location.origin).href;

const baseId = (item: PaletteItem) => (item.removable && item.id.startsWith('r-') ? item.id.slice(2) : item.id);

export function CommandPalette() {
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const toggle = useCommandPaletteStore((s) => s.toggle);
  const navigate = useNavigate();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const userId = useAuthStore((s) => s.user?.id) ?? 'anon';
  const isStaff = hasPermission('view_tickets');
  const canTasks = isStaff && hasPermission('view_sprints');
  const canWiki = useWikiAccess();
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [selected, setSelected] = useState(0);
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const [usage, setUsage] = useState<ReturnType<typeof loadUsage>>({});
  const [primedRun, setPrimedRun] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLElement>(null);

  const trimmed = query.trim();
  const q = useDebouncedValue(trimmed, 200);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAuthenticated, toggle]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setScope('all');
    setSelected(0);
    setRecent(loadRecent(userId));
    setUsage(loadUsage(userId));
    setPrimedRun(null);
  }, [open, userId]);

  const availableScopes = useMemo<Scope[]>(
    () =>
      SCOPES.map((s) => s.scope).filter(
        (s) => (s !== 'tasks' || canTasks) && (s !== 'people' || isStaff) && (s !== 'wiki' || canWiki),
      ),
    [canTasks, isStaff, canWiki],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  const commands = useMemo(
    () =>
      isAuthenticated
        ? buildCommands({
            hasPermission,
            canWiki,
            themeMode,
            setThemeMode,
            logout: keycloakLogout,
            copyCurrentLink: () => {
              void copyToClipboard(window.location.href).then((ok) =>
                ok ? toast.success('Ссылка на страницу скопирована') : toast.error('Не удалось скопировать'),
              );
            },
            clearHistory:
              recent.length > 0
                ? () => {
                    clearRecent(userId);
                    setRecent([]);
                    setUsage({});
                    toast.success('Недавнее очищено');
                  }
                : null,
          })
        : [],
    [isAuthenticated, hasPermission, canWiki, themeMode, setThemeMode, recent.length, userId],
  );

  const search = usePaletteSearch({ open, q, scope, isStaff, canTasks, canWiki });

  const remote = useMemo(
    () => hasRemoteSources({ q: trimmed, scope, isStaff, canTasks, canWiki }),
    [trimmed, scope, isStaff, canTasks, canWiki],
  );
  const run = `${scope}|${remote}|${trimmed !== ''}`;
  const settledNow = remote && search.settled === settledKey(trimmed, scope);
  const nextPrimed = settledNow ? run : remote ? primedRun : null;
  if (nextPrimed !== primedRun) setPrimedRun(nextPrimed);
  const awaitingFirst = remote && primedRun !== run && !settledNow;

  const { items, entitiesAt } = useMemo(() => {
    const out: PaletteItem[] = [];
    let entitiesAt: number | null = null;
    const wantCommands = scope === 'all' || scope === 'commands';

    if (!trimmed) {
      if (scope === 'all') {
        for (const r of recent) {
          if (r.kind === 'command') {
            const c = commands.find((x) => x.id === r.id);
            if (c) out.push({ ...commandToItem(c, 'recent'), id: `r-${c.id}`, removable: true });
          } else {
            out.push({
              id: `r-${r.id}`,
              group: 'recent',
              kind: r.kind,
              icon: RECENT_ICONS[r.kind],
              label: r.label,
              badge: r.badge,
              subtitle: r.hint,
              to: r.to,
              person: r.kind === 'person' ? { seed: r.id.slice(2) } : undefined,
              removable: true,
            });
          }
        }
        entitiesAt = out.length;
        for (const t of search.results.tasks) out.push({ ...t, group: 'mytasks' });
      }
      if (wantCommands) {
        for (const section of SECTION_ORDER) {
          for (const c of commands) if (c.section === section) out.push(commandToItem(c, section));
        }
      }
    } else if (wantCommands) {
      const scored = commands
        .map((c) => ({ c, m: matchCommand(c.label, c.keywords ?? [], trimmed) }))
        .filter((x): x is { c: Command; m: NonNullable<typeof x.m> } => x.m != null)
        .map((x) => ({ ...x, score: x.m.score + usageBoost(usage, x.c.id) }))
        .sort((a, b) => b.score - a.score);
      const cap = scope === 'commands' ? scored.length : 6;
      for (const { c, m } of scored.slice(0, cap)) out.push(commandToItem(c, 'commands', m.ranges));
    }

    if (scope !== 'commands' && !(scope === 'all' && !trimmed)) {
      const { tickets, tasks, wiki, people } = search.results;
      const entityOrder =
        looksLikeTaskKey(trimmed) ? [tasks, tickets, wiki, people] : [tickets, tasks, wiki, people];
      const entities = entityOrder.flat();
      if (looksLikeTicketNumber(trimmed) || looksLikeTaskKey(trimmed)) {
        entitiesAt = 0;
        out.unshift(...entities);
      } else {
        entitiesAt = out.length;
        out.push(...entities);
      }
    }

    if (trimmed.length >= MIN_QUERY) {
      const enc = encodeURIComponent(trimmed);
      const more = (id: string, label: string, to: string, icon: PaletteItem['icon']) =>
        out.push({ id, group: 'more', kind: 'more', icon, label, to, description: 'Откроет раздел с этим запросом в поиске' });
      if (scope === 'all' || scope === 'tickets') {
        more('m-tickets', `Все заявки по «${trimmed}»`, isStaff ? `/tickets/workspace?q=${enc}` : `/tickets?q=${enc}`, Search);
      }
      if (canTasks && (scope === 'all' || scope === 'tasks')) {
        more('m-tasks', `Задачи спринта по «${trimmed}»`, `/sprints?q=${enc}`, Search);
      }
      if (canWiki && (scope === 'all' || scope === 'wiki')) {
        more('m-wiki', `Все инструкции по «${trimmed}»`, `/instructions?q=${enc}`, Search);
      }
    }
    return { items: out, entitiesAt };
  }, [trimmed, scope, recent, commands, usage, search.results, isStaff, canTasks, canWiki]);

  useEffect(() => {
    setSelected(0);
  }, [trimmed, scope]);

  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, items.length - 1)));
  }, [items.length]);

  const record = useCallback(
    (item: PaletteItem) => {
      if (item.kind === 'more') return;
      const id = baseId(item);
      if (item.kind === 'command') {
        bumpUsage(userId, id);
        if (!item.to) return;
      }
      setRecent(
        pushRecent(userId, {
          id,
          kind: item.kind,
          label: item.label,
          badge: item.badge,
          hint: item.subtitle,
          to: item.to,
        }),
      );
    },
    [userId],
  );

  const pick = useCallback(
    (item: PaletteItem, mode: PickMode) => {
      if (mode === 'copy') {
        if (!item.to) return;
        void copyToClipboard(absoluteUrl(item.to)).then((ok) =>
          ok ? toast.success('Ссылка скопирована') : toast.error('Не удалось скопировать'),
        );
        return;
      }
      record(item);
      if (mode === 'newtab' && item.to) {
        window.open(absoluteUrl(item.to), '_blank', 'noopener');
        close();
        return;
      }
      if (item.run) {
        item.run();
        if (!item.stay) close();
        return;
      }
      if (item.to) {
        close();
        navigate(item.to);
      }
    },
    [record, close, navigate],
  );

  const hover = useCallback((index: number) => setSelected(index), []);

  const onInput = (value: string) => {
    const next = takePrefix(value, scope, availableScopes);
    if (next.scope !== scope) setScope(next.scope);
    setQuery(next.text);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const move = (delta: number) => {
      e.preventDefault();
      if (items.length === 0) return;
      setSelected((s) => (s + delta + items.length) % items.length);
    };
    const ctrlOnly = e.ctrlKey && !e.metaKey && !e.altKey;
    if (e.key === 'ArrowDown' || (ctrlOnly && e.key === 'n')) return move(1);
    if (e.key === 'ArrowUp' || (ctrlOnly && e.key === 'p')) return move(-1);
    if (e.key === 'PageDown') return move(Math.min(5, items.length - 1 - selected) || 0);
    if (e.key === 'PageUp') return move(-Math.min(5, selected) || 0);
    if (e.key === 'Tab') {
      e.preventDefault();
      setScope((s) => cycleScope(s, availableScopes, e.shiftKey ? -1 : 1));
      return;
    }
    if (e.key === 'Backspace' && query === '' && scope !== 'all') {
      e.preventDefault();
      setScope('all');
      return;
    }
    if (e.key === 'Delete' && query === '') {
      const item = items[selected];
      if (item?.removable) {
        e.preventDefault();
        setRecent(removeRecent(userId, baseId(item)));
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[selected];
      if (item) pick(item, e.metaKey || e.ctrlKey ? 'newtab' : e.shiftKey ? 'copy' : 'open');
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected, items]);

  if (!isAuthenticated) return null;

  const active = items[selected];
  const groups: { group: PaletteGroup; items: { item: PaletteItem; index: number }[] }[] = [];
  items.forEach((item, index) => {
    const last = groups[groups.length - 1];
    if (last && last.group === item.group) last.items.push({ item, index });
    else groups.push({ group: item.group, items: [{ item, index }] });
  });

  const { tickets, tasks, wiki, people } = search.results;
  const noEntities = tickets.length + tasks.length + wiki.length + people.length === 0;
  const skeletonAt = awaitingFirst && noEntities ? entitiesAt : null;
  const skeleton = (
    <div key="skeleton" className={styles.group} aria-hidden>
      <div className={styles.groupTitle}>
        <span className={styles.skeletonTitleRow}>
          <Skeleton className={styles.skeletonBar} width={72} />
        </span>
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.skeletonRow}>
          <Skeleton className={styles.skeletonIcon} width={28} height={28} />
          <span className={styles.skeletonMain}>
            <span className={styles.skeletonTitle}>
              <Skeleton className={styles.skeletonBar} width={`${[62, 48, 55][i]}%`} />
            </span>
            { }
            {i === 0 && (
              <span className={styles.skeletonSub}>
                <Skeleton className={styles.skeletonBar} width="34%" />
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
  const empty = EMPTY_HINTS[scope];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent flush className={styles.palette} aria-label="Командная палитра">
        <div className={styles.frame}>
          <div className={styles.searchRow}>
            <SearchInput
              ref={inputRef}
              label={scope === 'all' ? 'Поиск и команды' : `Искать: ${SCOPES.find((s) => s.scope === scope)?.label.toLowerCase()}`}
              value={query}
              onChange={onInput}
              onKeyDown={onKeyDown}
              className={styles.search}
              role="combobox"
              aria-expanded={items.length > 0}
              aria-controls="command-palette-list"
              aria-activedescendant={active?.id}
              aria-autocomplete="list"
              autoComplete="off"
              spellCheck={false}
            />
            { }
            <div className={styles.scopes} role="tablist" aria-label="Где искать">
              {availableScopes.map((s) => {
                const def = SCOPES.find((d) => d.scope === s)!;
                return (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    tabIndex={-1}
                    aria-selected={scope === s}
                    className={cn(styles.scope, scope === s && styles.scopeActive)}
                    title={def.prefix ? `Префикс «${def.prefix}»` : undefined}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setScope(s)}
                  >
                    {def.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.body}>
            <Scroller
              ref={listRef}
              id="command-palette-list"
              role="listbox"
              aria-label="Результаты"
              className={styles.results}
            >
              {search.altQuery && (
                <button type="button" className={styles.altNote} onClick={() => setQuery(search.altQuery ?? '')}>
                  Показано по «{search.altQuery}» — похоже, запрос набран в другой раскладке. Исправить запрос
                </button>
              )}
              {groups.map(({ group, items: groupItems }) => [
                skeletonAt === groupItems[0].index && skeleton,
                <div key={group} role="group" aria-label={GROUP_TITLES[group]} className={styles.group}>
                  <div className={styles.groupTitle} aria-hidden>
                    {GROUP_TITLES[group]}
                  </div>
                  {groupItems.map(({ item, index }) => (
                    <PaletteRow
                      key={item.id}
                      item={item}
                      index={index}
                      active={index === selected}
                      onHover={hover}
                      onPick={pick}
                    />
                  ))}
                </div>,
              ])}
              {skeletonAt === items.length && skeleton}
              {items.length === 0 && skeletonAt == null && (
                <EmptyState
                  compact
                  Icon={Search}
                  title={q.length >= MIN_QUERY ? `Ничего не нашлось по «${q}»` : empty.title}
                  hint={
                    q.length >= MIN_QUERY
                      ? scope === 'all'
                        ? 'Проверьте опечатки или поищите другими словами.'
                        : 'Tab переключает область — возможно, это в другой.'
                      : empty.hint
                  }
                />
              )}
            </Scroller>
            <PalettePreview item={active} mod={MOD} />
          </div>

          <div className={styles.footer}>
            <span className={styles.footerHint}>
              <kbd className={styles.kbd}>↑↓</kbd> выбор
            </span>
            <span className={styles.footerHint}>
              <kbd className={styles.kbd}>Enter</kbd> открыть
            </span>
            <span className={styles.footerHint} data-wide>
              <kbd className={styles.kbd}>{MOD}Enter</kbd> в новой вкладке
            </span>
            <span className={styles.footerHint} data-wide>
              <kbd className={styles.kbd}>Tab</kbd> область
            </span>
            <span className={styles.footerBusy} aria-live="polite">
              {search.loading ? 'ищем…' : trimmed && items.length > 0 ? `${items.length} в выдаче` : ''}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
